<template>
  <b-modal
    v-model="active"
    class="modal"
    centered
    hide-footer
    no-close-on-backdrop
    no-close-on-esc
  >
    <template #modal-title>
      Выдать{{ !hasType ? ' ТМЦ' : '' }}
      <span
        v-if="hasType"
        class="type-name"
      >
        {{ passedType.name }}
      </span>
    </template>
    <Loader
      v-if="!isInitialized && busy"
      label="Данные загружаются"
      :height="!hasType ? 405 : 327"
      :active="true"
    />
    <template v-else>
      <template v-if="hasType && !items.length">
        <div class="notice">
          Для данного типа нет ТМЦ. Обратитесь к администратору. 
        </div>
        <div class="actions">
          <Button
            label="Закрыть"
            @click="close"
          />
        </div>
      </template>
      <vee-observer
        v-else
        v-slot="{ handleSubmit, errors }"
        slim
      >
        <form
          class="form"
          @submit.prevent="handleSubmit(submit)"
        >
          <div class="inputs">
            <vee-provider
              v-if="!hasType"
              vid="type"
              rules="required"
              slim
            >
              <Input
                v-model="form.type"
                floating-label="Категория/подкатегория/тип ТМЦ"
                type="select"
                :items="types"
                :error="errors.type"
                :with-categorized="true"
                :required-mark="true"
                :clearable="true"
                :searchable="true"
                :actually-normal-error-messages="true"
              />
            </vee-provider>
            <vee-provider
              vid="item"
              rules="required"
              slim
            >
              <Input
                v-model="form.item"
                floating-label="ТМЦ"
                type="select"
                :items="items"
                :error="errors.item"
                :disabled="busy"
                :required-mark="true"
                :clearable="true"
                :searchable="true"
                :actually-normal-error-messages="true"
              />
            </vee-provider>
            <div class="inputs__row">
              <div class="date-input">
                <vee-provider
                  vid="date"
                  rules="required|date"
                  slim
                >
                  <Input
                    ref="calendar"
                    v-model="form.issueDate"
                    label="Дата выдачи"
                    placeholder="00.00.0000"
                    format="DD.MM.YYYY, dddd"
                    type="date"
                    icon="calendar"
                    :error="errors.date"
                    :editable="true"
                    :icon-right="true"
                    :required-mark="true"
                    :actually-normal-error-messages="true"
                  />
                </vee-provider>
              </div>
              <Input
                v-model="form.issuer"
                floating-label="Кто выдал"
                type="select"
                :items="employees"
                :searchable="true"
                :clearable="true"
              />
            </div>
            <vee-provider
              vid="comment"
              rules="max:500"
              slim
            >
              <InputOld
                v-model="form.comment"
                floating-label="Комментарий"
                type="textarea"
                max-length="500"
                :clearable="true"
              />
            </vee-provider>
            <FileLoader
              ref="fileLoader"
              v-model="form.files"
              actions="loadFiles,preview"
              format="all"
              :limit="10"
            />
          </div>
          <div class="actions">
            <Button
              label="Отменить"
              @click="close"
            />
            <Button
              label="Выдать"
              color="primary"
              type="submit"
              :disabled="busy"
            />
          </div>
        </form>
      </vee-observer>
    </template>
  </b-modal>
</template>

<script>
import moment from "moment";
import serialize from "json-form-data";

import api from "@/plugins/api";

import Loader from "@/components/loader.vue";
import Input from "@/components/controls2/Input.vue";
import InputOld from "@/components/controls/input-old.vue";
import Button from "@/components/controls2/Button.vue";
import FileLoader from "@/components/file-loader.vue";

export default {
  name: "issue-item-modal",
  components: {
    Loader,
    Input,
    InputOld,
    Button,
    FileLoader,
  },
  props: {
    workerId: {
      type: [String, Number],
      required: true,
    },
  },
  data: () => ({
    isInitialized: false,
    busy: false,
    active: false,
    types: [],
    employees: [],
    items: [],
    form: {
      type: null,
      item: null,
      issueDate: "",
      issuer: null,
      comment: "",
      files: [],
    },
    passedType: null,
    worker: null,
  }),
  computed: {
    currentUserId () {
      return Number(this.$store.state.userId);
    },
    hasType () {
      return !!this.passedType;
    },
  },
  watch: {
    "form.type": async function (type) {
      this.form.item = null;

      this.busy = true;

      await this.loadItems(type);

      this.busy = false;
    },
  },
  methods: {
    async open(type = null) {
      this.passedType = type;
      this.items = [];

      this.isInitialized = false;
      this.busy = true;

      this.active = true;

      try {
        await this.loadEmployees();
        await this.loadWorker();

        this.form.type = null;
        this.form.item = null;
        this.form.issueDate = moment().format("DD.MM.YYYY");
        this.form.issuer = this.employees.find(employee => employee.value === this.currentUserId);
        this.form.comment = "";
        this.form.files = [];

        if (!type) {
          await this.loadTypes();
        } else {
          await this.loadItems({
            value: type.id,
          });
        }

        this.isInitialized = true;
      } catch (error) {
        console.error(error);
        this.close();
      }

      this.busy = false;

    },
    close() {
      this.active = false;
      this.$emit("close");
      
      if (this.$refs.fileLoader) {
        this.$refs.fileLoader.reset();
      }
    },
    async loadTypes() {
      const types = [];

      const { data } = await api.get("inventory-items/get-tree");

      data.forEach((category) => {
        const categoryLabel = `<b>${category.name}</b>`;

        if (category.types) {
          category.types.forEach(type => types.push({
            value: type.id,
            label: `${category.name} ${type.name}`,
            formatLabel: `${categoryLabel}/${type.name}`,
            isSubCategory: true,
          }));
        }

        if (category.categories) {
          category.categories.forEach((subCategory) => {
            const subcategoryLabel = `${categoryLabel}/${subCategory.name}`;

            if (subCategory.types) {
              subCategory.types.forEach(type => types.push({
                value: type.id,
                label: `${category.name} ${subCategory.name} ${type.name}`,
                formatLabel: `${subcategoryLabel}/${type.name}`,
                isSubCategory: true,
              }));
            }
          });
        }
      });

      this.types = types;
    },
    async loadItems(type) {
      if (!type) {
        this.items = [];
        return;
      }

      try {
        const { data } = await api.get("inventory/index", {
          params: {
            "global[type]": type.value,
            "status": "0",
          },
        });

        this.items = data.map(({ id, name, guid }) => ({
          value: id,
          label: `${guid} / ${name || "—"}`,
          guid,
        }));
      } catch (error) {
        console.error(error);
      }
    },
    async loadEmployees() {
      const { response } = await this.$store.dispatch(
        "personalEmployee/getWorkersList",
        this.currentUserId,
      );

      this.employees = response.map((item) => ({
        value: item.id,
        label: item.name,
        isUser: item.is_user,
      }));
    },
    async loadWorker () {
      const { response } = await this.$store.dispatch(
        "personalEmployee/getUserInfo",
        this.workerId,
      );

      this.worker = response;
    },
    async submit() {
      this.busy = true;

      try {
        const { item, issuer } = this.form;

        const params = {
          action: 0,
          worker_id: this.workerId,
          inventory_id: item.value,
          date: this.form.issueDate,
          comment: this.form.comment,
        };

        if (issuer) {
          if (issuer.isUser) {
            params.author_user_id = issuer.value;
          } else {
            params.author_worker_id = issuer.value;
          }
        }

        const formData = serialize(params);

        this.form.files.forEach(file => formData.append("files[]", file));

        const { data } = await api.post("inventory-history/create", formData);

        this.$emit("submit", data);

        this.$notify({
          group: "success-message",
          data: {
            textMessage: `Позиция <b>№${item.guid} ${item.label}</b> успешно выдана сотруднику <b>${this.worker.name}</b>`,
          },
        });

        this.close();
      } catch (error) {
        console.error(error);

        this.$notify({
          group: "error-message",
          data: {
            textMessage: "Произошла ошибка. Попробуйте ещё раз",
          },
        });

        this.busy = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/vars';

::v-deep .modal .modal-dialog {
  max-width: 590px;

  @include onlyMobile {
    margin: 0;
  }
}

.form {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  @include onlyMobile {
    padding-bottom: 40px;
  }
}

.type-name {
  font-weight: 400;
}

.inputs {
  display: flex;
  flex-direction: column;
  margin-bottom: 25px;
  gap: 25px;

  &__row {
    display: flex;
    align-items: baseline;
    gap: inherit;

    > div {
      flex-grow: 1;
    }
  }

  @include onlyMobile {
    &__row {
      flex-direction: column;
      align-items: stretch;
    }
  }
}

.actions {
  display: flex;
  justify-content: flex-end;
  margin-top: auto;
  gap: 10px;

  @include onlyMobile {
    justify-content: stretch;
    gap: 0;
    position: absolute;
    left: 0;
    bottom: 0;
    right:0;
    background: #fff;
    z-index: 10;
    > button {
      flex-grow: 1;
      border-radius: 0;
      border-left: none;
      border-right: none;
      border-bottom: none;
    }
  }
}

.date-input {
  max-width: 125px;
}

.notice {
  margin-bottom: 25px;
  color: $color-label;
  font-size: 13px;
  line-height: 120%;
}

</style>
